import React, { useEffect, useState } from "react";
import styles from "./ExampleQuestions.module.css";

interface ExampleQuestion {
  text: string;
  tag: string;
  tagLabel: string;
}

interface ExampleQuestionsData {
  categories: Array<{
    id: string;
    label: string;
    icon: string;
  }>;
  questions: ExampleQuestion[];
}

interface ExampleQuestionsProps {
  onQuestionSelect: (question: string) => void;
  expertMode: boolean;
  onExpertModeChange: (value: boolean) => void;
}

export const ExampleQuestions: React.FC<ExampleQuestionsProps> = ({
  onQuestionSelect,
  expertMode,
  onExpertModeChange,
}) => {
  const [data, setData] = useState<ExampleQuestionsData | null>(null);
  const [activeFilter, setActiveFilter] = useState<string | null>(null);
  const [isLoaded, setIsLoaded] = useState(false);

  useEffect(() => {
    fetch("/example-questions.json")
      .then((res) => res.json())
      .then((json) => {
        setData(json);
        setTimeout(() => setIsLoaded(true), 100);
      })
      .catch((err) => console.error("Failed to load example questions:", err));
  }, []);

  if (!data) return null;

  const filteredQuestions = activeFilter
    ? data.questions.filter((q) => q.tag === activeFilter)
    : data.questions;

  // Limit to 6 questions for display
  const displayQuestions = filteredQuestions.slice(0, 6);

  const getTagClass = (tag: string) => {
    switch (tag) {
      case "beginner":
        return styles.tagBeginner;
      case "operations":
        return styles.tagOperations;
      case "pro":
        return styles.tagPro;
      default:
        return "";
    }
  };

  return (
    <div className={`${styles.container} ${isLoaded ? styles.loaded : ""}`}>
      {/* Header */}
      <div className={styles.header}>
        <div className={styles.iconWrapper}>
          <svg
            className={styles.headerIcon}
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            {/* Fixed-wing UAS drone - angled view */}
            {/* Fuselage */}
            <path
              d="M4 12.5L12 8L20 12.5L12 14L4 12.5Z"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            {/* Wings */}
            <path
              d="M2 13L12 10L22 13"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            {/* Tail */}
            <path
              d="M12 14L12 18L9 17M12 18L15 17"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            {/* Nose */}
            <path
              d="M12 8L12 6"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
            />
          </svg>
        </div>
        <h1 className={styles.title}>UAS Policy Navigator</h1>
        <p className={styles.subtitle}>
          Ask questions about UK CAA guidance and assimilated law
        </p>
      </div>

      {/* Filter Pills */}
      <div className={styles.filterRow}>
        <button
          className={`${styles.filterPill} ${activeFilter === null ? styles.filterActive : ""}`}
          onClick={() => setActiveFilter(null)}
        >
          All
        </button>
        {data.categories.map((cat) => (
          <button
            key={cat.id}
            className={`${styles.filterPill} ${activeFilter === cat.id ? styles.filterActive : ""} ${styles[`filter${cat.id.charAt(0).toUpperCase() + cat.id.slice(1)}`]}`}
            onClick={() => setActiveFilter(cat.id === activeFilter ? null : cat.id)}
          >
            {cat.label}
          </button>
        ))}
        <div className={styles.filterDivider} />
        <label className={styles.expertToggle}>
          <input
            type="checkbox"
            checked={expertMode}
            onChange={(e) => onExpertModeChange(e.target.checked)}
          />
          <span className={styles.expertToggleSlider} />
          <span className={styles.expertToggleLabel}>Expanded Responses</span>
        </label>
      </div>

      {/* Question Grid */}
      <div className={styles.grid}>
        {displayQuestions.map((question, index) => (
          <button
            key={index}
            className={styles.card}
            onClick={() => onQuestionSelect(question.text)}
            style={{ animationDelay: `${index * 80}ms` }}
          >
            <span className={`${styles.tag} ${getTagClass(question.tag)}`}>
              {question.tagLabel}
            </span>
            <span className={styles.questionText}>{question.text}</span>
            <div className={styles.cardArrow}>
              <svg
                width="16"
                height="16"
                viewBox="0 0 16 16"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M3.5 8h9m0 0L9 4.5m3.5 3.5L9 11.5"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </div>
          </button>
        ))}
      </div>

      {/* Keyboard hint */}
      <div className={styles.hint}>
        <span className={styles.hintText}>Or type your own question below</span>
        <div className={styles.hintDivider} />
      </div>
    </div>
  );
};
